import { z } from "zod";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { localDateSchema, moneySchema, quantitySchema, timestampSchema, uuidSchema, } from "../common/primitives.js";
import { limitPolicySchema } from "../counterparties/counterparties.js";
import { fleetAccountStatusSchema, fleetBillingCycleSchema, vehicleTagKindSchema, vehicleTagStatusSchema, } from "./enums.js";
import { fleetPolicyLayerSchema } from "./policies.js";
/**
 * Filo hesabı (ADR 0017 §11): filo = `FLEET_CUSTOMER` rollü cari. Hesap satırı carinin filo AYARIDIR (fatura dönemi,
 * portal, durum); kredi limiti carinin limitidir (Prompt 07) ve burada yalnızca okunur.
 *
 * KVKK: sürücü adı / telefonu ve plaka `customers.personal.view` yoksa MASKELİ döner (`personalDataMasked: true`).
 */
const versionSchema = z.number().int().positive().describe("İyimser kilit sürümü");
const optionalNote = z.string().trim().max(1000).nullable();
export const fleetAccountSchema = z.object({
    id: uuidSchema,
    counterpartyId: uuidSchema,
    counterpartyCode: z.string(),
    counterpartyName: z.string(),
    billingCycle: fleetBillingCycleSchema,
    portalEnabled: z.boolean(),
    status: fleetAccountStatusSchema,
    notes: z.string().nullable(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
    version: z.number().int(),
});
/** Liste satırı: `FLEET_CUSTOMER` rollü her cari; filo ayarı açılmamışsa `account = null`. */
export const fleetAccountListItemSchema = z.object({
    counterpartyId: uuidSchema,
    counterpartyCode: z.string(),
    counterpartyName: z.string(),
    counterpartyActive: z.boolean(),
    counterpartyBlocked: z.boolean(),
    account: fleetAccountSchema.nullable(),
    activeVehicleCount: z.number().int(),
});
export const fleetAccountPageSchema = pageOf(fleetAccountListItemSchema);
export const listFleetAccountsQuerySchema = pageQuerySchema.extend({
    q: z.string().trim().min(1).max(100).optional().describe("Cari kodu ya da unvanı"),
    status: z
        .enum(["ACTIVE", "SUSPENDED", "UNCONFIGURED"])
        .optional()
        .describe("UNCONFIGURED: filo ayarı açılmamış cariler"),
});
export const createFleetAccountSchema = z.object({
    counterpartyId: uuidSchema,
    billingCycle: fleetBillingCycleSchema.default("MONTHLY"),
    portalEnabled: z.boolean().default(false),
    status: fleetAccountStatusSchema.default("ACTIVE"),
    notes: optionalNote.default(null),
});
export const updateFleetAccountSchema = z.object({
    billingCycle: fleetBillingCycleSchema.optional(),
    portalEnabled: z.boolean().optional(),
    status: fleetAccountStatusSchema.optional(),
    notes: optionalNote.optional(),
    version: versionSchema,
});
export const fleetCounterpartyParamsSchema = z.object({ counterpartyId: uuidSchema });
export const fleetVehicleParamsSchema = z.object({ vehicleId: uuidSchema });
// ── Araç ────────────────────────────────────────────────────────────────────
export const fleetVehicleSchema = z.object({
    id: uuidSchema,
    counterpartyId: uuidSchema,
    plate: z.string().describe("KVKK: normalize plaka (maskeli olabilir)"),
    fuelProductId: uuidSchema.nullable(),
    fuelProductName: z.string().nullable(),
    brand: z.string().nullable(),
    model: z.string().nullable(),
    modelYear: z.number().int().nullable(),
    status: z.enum(["ACTIVE", "INACTIVE"]),
    hasPolicyOverride: z.boolean().describe("Araca özel kural katmanında en az bir alan dolu"),
    activeTagCount: z.number().int(),
    createdAt: timestampSchema,
    version: z.number().int(),
    personalDataMasked: z.boolean(),
});
export const fleetVehicleListSchema = z.object({ items: z.array(fleetVehicleSchema) });
/** Araç customers modülünün `createVehicleInTx`'iyle açılır (sahibi = filo carisi). */
export const createFleetVehicleSchema = z.object({
    plate: z.string().trim().min(2).max(20).describe("KVKK: plaka (büyük harf, boşluksuz saklanır)"),
    fuelProductId: uuidSchema.nullable().default(null),
    brand: z.string().trim().min(1).max(60).nullable().default(null),
    model: z.string().trim().min(1).max(60).nullable().default(null),
    modelYear: z.number().int().min(1950).max(2100).nullable().default(null),
    notes: z.string().trim().max(500).nullable().default(null),
});
// ── Sürücü ──────────────────────────────────────────────────────────────────
const phoneSchema = z
    .string()
    .trim()
    .regex(/^\+[1-9][0-9]{7,14}$/, "Telefon E.164 biçiminde olmalıdır (ör. +905321234567)");
const cardNoSchema = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[0-9A-Z-]{3,40}$/, "Kart no 3–40 harf / rakam / tire olmalıdır");
export const fleetDriverSchema = z.object({
    id: uuidSchema,
    counterpartyId: uuidSchema,
    fullName: z.string().describe("KVKK (maskeli olabilir)"),
    phone: z.string().nullable().describe("KVKK (maskeli olabilir)"),
    cardNo: z.string().nullable(),
    status: z.enum(["ACTIVE", "INACTIVE", "ANONYMIZED"]),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
    version: z.number().int(),
    personalDataMasked: z.boolean(),
});
export const fleetDriverListSchema = z.object({ items: z.array(fleetDriverSchema) });
export const createFleetDriverSchema = z.object({
    fullName: z.string().trim().min(2).max(120).describe("KVKK"),
    phone: phoneSchema.nullable().default(null).describe("KVKK"),
    cardNo: cardNoSchema
        .nullable()
        .default(null)
        .describe("Sürücü kartı (kiracıda etkin kartlar arasında tekil)"),
});
export const updateFleetDriverSchema = z.object({
    fullName: z.string().trim().min(2).max(120).optional(),
    phone: phoneSchema.nullable().optional(),
    cardNo: cardNoSchema.nullable().optional(),
    status: z.enum(["ACTIVE", "INACTIVE"]).optional(),
    version: versionSchema,
});
// ── TTS ortamı (etiket / taşıt kartı) ───────────────────────────────────────
export const vehicleTagSchema = z.object({
    id: uuidSchema,
    vehicleId: uuidSchema,
    counterpartyId: uuidSchema,
    kind: vehicleTagKindSchema,
    tagUid: z.string(),
    status: vehicleTagStatusSchema,
    validFrom: localDateSchema.nullable(),
    validUntil: localDateSchema.nullable(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
    version: z.number().int(),
});
export const vehicleTagListSchema = z.object({ items: z.array(vehicleTagSchema) });
const tagUidSchema = z
    .string()
    .trim()
    .regex(/^[0-9A-Za-z:_-]{3,64}$/, "Etiket kimliği 3–64 harf / rakam / : _ - olmalıdır");
export const createVehicleTagSchema = z
    .object({
    kind: vehicleTagKindSchema,
    tagUid: tagUidSchema,
    validFrom: localDateSchema.nullable().default(null),
    validUntil: localDateSchema.nullable().default(null),
})
    .refine((value) => !value.validFrom || !value.validUntil || value.validUntil >= value.validFrom, {
    path: ["validUntil"],
    message: "Bitiş başlangıçtan önce olamaz",
});
/**
 * `LOST` (kayıp) SONLUDUR: kayıp etiket yeniden etkinleştirilemez (çalınmış olabilir) — yeni etiket tanımlanır.
 * `ACTIVE ↔ INACTIVE` serbesttir; etkin etiket kimliği kiracıda tekildir.
 */
export const updateVehicleTagSchema = z.object({
    status: vehicleTagStatusSchema.optional(),
    validFrom: localDateSchema.nullable().optional(),
    validUntil: localDateSchema.nullable().optional(),
    version: versionSchema,
});
// ── Hesap ayrıntısı ────────────────────────────────────────────────────────
export const fleetPeriodUsageSchema = z.object({
    day: localDateSchema,
    month: z.string().describe("YYYY-MM"),
    dayLitres: quantitySchema,
    dayAmount: moneySchema,
    monthLitres: quantitySchema,
    monthAmount: moneySchema,
});
export const fleetCreditInfoSchema = z.object({
    companyId: uuidSchema,
    companyName: z.string(),
    creditLimit: moneySchema.nullable(),
    totalRisk: moneySchema,
    available: moneySchema.nullable(),
    limitPolicy: limitPolicySchema,
    isBlocked: z.boolean(),
});
export const fleetAccountDetailSchema = z.object({
    counterparty: z.object({
        id: uuidSchema,
        code: z.string(),
        name: z.string(),
        isActive: z.boolean(),
        isBlocked: z.boolean(),
    }),
    account: fleetAccountSchema.nullable(),
    companies: z
        .array(z.object({ id: uuidSchema, name: z.string() }))
        .describe("Carinin bağlı olduğu ve kullanıcının `fleet.read` kapsamında gördüğü şirketler (dönem faturası şirket seçimi); " +
        "`counterparty.limit.read`'den bağımsız"),
    credit: z
        .array(fleetCreditInfoSchema)
        .describe("Carinin bağlı olduğu (ve görülebilen) şirketler; `counterparty.limit.read` yoksa boş"),
    defaultPolicy: fleetPolicyLayerSchema,
    vehicles: z.array(fleetVehicleSchema),
    drivers: z.array(fleetDriverSchema),
    tags: z.array(vehicleTagSchema),
    usage: fleetPeriodUsageSchema.describe("Cari genelinde bugün / bu ay (tüm istasyonlar, tüm araçlar)"),
    suggestedBillingPeriod: z.object({ from: localDateSchema, to: localDateSchema }).nullable(),
});
//# sourceMappingURL=accounts.js.map